import React, { useEffect, useState, useContext } from "react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { Link, useLocation } from "react-router-dom";
import { useParams } from "react-router";
import { PlanItem, UserItem } from "../../../models/user";
import SignupForm from "./signupForm";
import { PlanType, SubscriptionProductType } from "../../../models/plans";
import { PageSettingItem } from "../../../models/pageSettings";
import { LoaderDots } from "../../../middleware/main_loader";
import { GET_PLANS } from "../purchaseplan/queries";
import OrderSummary from "./orderSummary";
import PaymentSection from "./payment";
import { DataContext } from "../../../context/data-context";
import { useQueryParams } from "../../../middleware/common-functions";
import { GETBOOOK } from "./queries";
import { ReaderItem } from "../../../models/reader";
import videos from "./videos.json";
import VideoContainer from "./videoContainer";

type SectionsType = false | "signup" | "payment" | "giveaway";

const TeacherSignup: React.FC = () => {
  const location = useLocation();
  const dataContext = useContext(DataContext);
  const [pageType, setPageType] = useState<SectionsType>(false);
  const params = useQueryParams(window.location);
  const has_book = params.get("b");
  const has_video = params.get("v");
  const autplay_video = params.get("a");
  const video = has_video
    ? videos.find((v) => v.url === has_video) || false
    : false;
  const [section, setSection] = useState<SectionsType>("signup");
  const [products, setProducts] = useState<SubscriptionProductType[]>([
    {
      id: "1",
      plan_id: "1",
      name: "Flangoo Library™ - Monthly",
      secondary_pricing: 0,
      secondary_pricing_regular: 0,
    },
    {
      id: "2",
      plan_id: "3",
      name: "Flangoo Library™ - Annual",
      secondary_pricing: 0,
      secondary_pricing_regular: 0,
    },
    {
      id: "3",
      plan_id: "3",
      product_id: ["1"],
      name: "Flangoo Creator™ - Annual",
      sub: "(includes Flangoo Library)",
      secondary_pricing: 49,
      secondary_pricing_regular: 149,
    },
  ]);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionProductType>(
    products[0]
  );

  const { product_id } = useParams<{
    product_id?: string;
  }>();
  const [fetchPlans, { data: dataPlans, called: fetchedPlans }] = useLazyQuery<{
    plans: PlanType[];
    pageSettings: PageSettingItem[];
  }>(GET_PLANS, { fetchPolicy: "no-cache" });
  const {
    data: dataBooks,
    loading: gettingBook,
    error: errorOnBook,
  } = useQuery<{
    book?: ReaderItem;
  }>(GETBOOOK, {
    fetchPolicy: "no-cache",
    skip: !has_book,
    variables: { book_title: has_book },
  });

  const plans_data: PlanItem[] = JSON.parse(
    localStorage.getItem("plans_data") || "{}"
  );
  const trial = plans_data.find((plan) => plan.id === "2");
  let title = `Sign up for a free ${trial?.days}-day trial of our online ereader library`;
  let sub_title = "No credit card required.";
  if (pageType === "payment") {
    title = "Create Your Account";
    sub_title = "";
  } else if (pageType === "giveaway") {
    title = `Sign up for a free trial through June 30 of our online ereader library`;
    sub_title = "";
  }
  useEffect(() => {
    if (errorOnBook) {
      console.log("index.tsx:59 | errorOnBook", errorOnBook);
    }
  }, [errorOnBook]);

  useEffect(() => {
    const document_body = document.getElementsByTagName("body")[0];
    const currentPath = location.pathname;
    let page_type: SectionsType = "signup";
    if (currentPath.includes("payment")) {
      page_type = "payment";
    } else if (currentPath.includes("create_account")) {
      page_type = "giveaway";
    }
    if (document_body) {
      document_body.classList.add("white_bg");
    }
    setPageType(page_type);
    if (page_type === "payment" && fetchedPlans === false) {
      fetchPlans();
    }
    return () => {
      document_body.classList.remove("white_bg");
    };
    //eslint-disable-next-line
  }, [location]);

  useEffect(() => {
    if (dataPlans?.plans) {
      const set_products = products.map((product) => {
        const plan: PlanType | undefined = dataPlans.plans.find(
          (p) => p.id === product.plan_id
        );
        if (plan) {
          return {
            ...product,
            plan,
            price: plan.price_amount + product.secondary_pricing,
          };
        }
        return product;
      });
      setProducts(set_products);
      if (product_id) {
        const selectedPlanset = set_products.find(
          (plan) => plan.id === product_id
        );
        if (selectedPlanset) {
          setSelectedPlan(selectedPlanset);
        }
      }
    }
    //eslint-disable-next-line
  }, [dataPlans, product_id]);

  const aftersignup = (signup_data: UserItem) => {
    signup_data.is_paying = true;
    dataContext.setUser(signup_data);
    setSection("payment");
  };

  const renderSection = () => {
    switch (section) {
      case "payment":
        return (
          <PaymentSection
            selectedPlan={selectedPlan!}
            paypal_setting={
              dataPlans && dataPlans.pageSettings
                ? dataPlans.pageSettings[0]
                : undefined
            }
          />
        );

      default:
        return (
          <SignupForm
            button_txt={
              pageType === "payment" ? "Continue to Payment" : undefined
            }
            promocode={pageType === "giveaway" ? "GIVEAWAY2024" : undefined}
            nextAction={pageType === "payment" ? aftersignup : undefined}
          />
        );
    }
  };

  return (
    <div className="container first-container px-md-0 mt-0 pt-4 signup_page">
      <HeaderBanner />
      <hr />
      <div className="row justify-content-between">
        {pageType === "giveaway" ? <GiveawaySection /> : null}
        <div className="col-lg-5">
          <h4 className="black_text">{title}</h4>
          {renderSection()}
          <p className="black_text text-center mt-2">{sub_title}</p>

          {pageType === "payment" && section === "signup" ? (
            <small className="text-muted d-block text-center">
              Payment details on the next step
            </small>
          ) : null}
          <br />
          <br />
          {section === "signup" ? (
            <p>
              Already have an account?{" "}
              <Link to="/signin" className="text-primary">
                Sign In
              </Link>{" "}
            </p>
          ) : null}
        </div>
        {pageType === "payment" ? (
          <div className="col-lg-5">
            {dataPlans?.plans ? (
              <OrderSummary selectedPlan={selectedPlan} />
            ) : (
              <LoaderDots size={20} />
            )}
          </div>
        ) : pageType === "giveaway" ? null : video ? (
          <div className="col-lg-7">
            <VideoContainer
              video_url={video.video}
              poster={video.poster}
              autoplay={autplay_video ? "muted" : false}
            />{" "}
          </div>
        ) : (
          <BookInfo book={dataBooks?.book} loading={gettingBook} />
        )}
      </div>
    </div>
  );
};

const GiveawaySection = () => (
  <div className="col-lg-7">
    <div className="video_item hover">
      <video
        controls={true}
        autoCorrect="true"
        // onPause={handlePlayVideo}
        // ref="https://tupuedes.b-cdn.net/flg/testimonial_martha_flangoo.mp4"
        controlsList="nodownload"
      >
        <source
          src="https://tupuedes.b-cdn.net/flg/FGPromo01.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>
    </div>
  </div>
);

type BookInfoProps = {
  book?: ReaderItem;
  loading: boolean;
};
const BookInfo: React.FC<BookInfoProps> = ({ book, loading }) => (
  <div className="col-lg-6">
    <div className="row">
      <div className="col-md-6">
        {loading ? null : book ? (
          <img src={book.images.cover} alt="" className="img-fluid" />
        ) : (
          <img
            src="https://flangoo-cdn.s3.us-east-2.amazonaws.com/2021-08-03_white.gif"
            className="img-fluid"
            alt="books animation"
          />
        )}
      </div>
      <div className="col-md-6 d-flex align-items-center">
        <ul>
          <li>Give your students the confidence to use a new language</li>
          <li>
            Connect them with the cultures you fell in love with through an
            online library of over 300 fully-narrated ereaders
          </li>
          <li>
            Enjoy teaching your students without spending all day writing a
            lesson plan
          </li>
        </ul>
      </div>
    </div>
    <hr />
    <p>
      <i>
        "They aren't just picking up grammar and vocabulary [with Flangoo], it's
        a love of reading and culture."
      </i>
    </p>
    <p className="text-right"> —Teacher Dagnah T.</p>
  </div>
);

const HeaderBanner = () => (
  <div className="signup_banner">
    <h1 className="text-center ">
      Don’t wait until next fall. <br />
      Purchase a yearly subscription now and we will extend your subscription
      through the <span>next school year (June 30, 2026). </span>
    </h1>
    <img
      src="https://flangoo-cdn.s3.us-east-2.amazonaws.com/signup_banner-laptop.png"
      alt="signup_banner"
      className="img-fluid "
    />
  </div>
);

export default TeacherSignup;
